import { useEffect, useState } from "react";
import { Plus, Trash2, ArrowUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { selectCls, btnBrand } from "@/lib/academyFormat";
import NotionCard from "@/components/academy/NotionCard";
import EmailCard from "@/components/academy/EmailCard";
import { ACADEMY } from "@/constants/testIds";

const FIELD_TYPES = { text: "Texto", textarea: "Texto largo", email: "Email", phone: "Teléfono", date: "Fecha", select: "Opciones", file: "Archivo (foto o PDF)" };
const lines = (txt) => txt.split("\n").map((s) => s.trim()).filter(Boolean);

function Section({ title, text, children }) {
  return (
    <div className="bg-white border border-border rounded-xl p-5 space-y-4">
      <div>
        <p className="font-medium text-foreground">{title}</p>
        {text && <p className="text-sm text-muted-foreground">{text}</p>}
      </div>
      {children}
    </div>
  );
}

// Fix U (2026-10-13): configuracion general de la academia.
export default function AcademySettingsPage() {
  const { toast } = useToast();
  const [s, setS] = useState(null);
  const [sources, setSources] = useState("");
  const [categories, setCategories] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/academy/settings").then(({ data }) => {
      setS({ ...data, commission_tiers: data.commission_tiers.map((t) => ({ up_to: t.up_to == null ? "" : String(t.up_to), percent: String(t.percent) })) });
      setSources((data.lead_sources || []).join("\n"));
      setCategories((data.expense_categories || []).join("\n"));
    }).catch((err) => toast({ title: "Error", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" }));
  }, [toast]);

  if (!s) return <p className="text-muted-foreground py-12 text-center">Cargando...</p>;
  const set = (k) => (e) => setS((x) => ({ ...x, [k]: e.target.value }));
  const setTier = (i, k, v) => setS((x) => ({ ...x, commission_tiers: x.commission_tiers.map((t, j) => (j === i ? { ...t, [k]: v } : t)) }));
  const setField = (i, k, v) => setS((x) => ({ ...x, form_fields: x.form_fields.map((f, j) => (j === i ? { ...f, [k]: v } : f)) }));
  const moveField = (i) => setS((x) => {
    const ff = [...x.form_fields];
    [ff[i - 1], ff[i]] = [ff[i], ff[i - 1]];
    return { ...x, form_fields: ff };
  });

  const save = async () => {
    setSaving(true);
    try {
      const tiers = s.commission_tiers.filter((t) => t.percent !== "").map((t, i, arr) => ({
        up_to: i === arr.length - 1 || t.up_to === "" ? null : Number(t.up_to), percent: Number(t.percent),
      }));
      const body = {
        academy_name: s.academy_name, reservation_amount: Number(s.reservation_amount) || 0, no_show_hours: Number(s.no_show_hours) || 24,
        commission_mode: s.commission_mode, commission_tiers: tiers, lead_sources: lines(sources), expense_categories: lines(categories),
        commission_include_reservations: s.commission_include_reservations !== false, instructor_base: s.instructor_base || "collected", // Fix X (2026-10-13)
        form_fields: s.form_fields.filter((f) => f.label.trim()).map((f) => ({
          key: f.key || null, label: f.label.trim(), type: f.type, required: Boolean(f.required),
          options: typeof f.options === "string" ? f.options.split(",").map((o) => o.trim()).filter(Boolean) : f.options || [],
        })),
        contract_text: s.contract_text || "", email_from_name: s.email_from_name || "", email_reply_to: s.email_reply_to || "",
        notion_database_id: s.notion_database_id || "", // Fix Z (2026-10-13)
      };
      const { data } = await api.put("/academy/settings", body);
      setS({ ...data, saved_notion_id: data.notion_database_id, commission_tiers: data.commission_tiers.map((t) => ({ up_to: t.up_to == null ? "" : String(t.up_to), percent: String(t.percent) })) });
      toast({ title: "Configuración guardada" });
    } catch (err) {
      toast({ title: "No se pudo guardar", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid={ACADEMY.settingsPage}>
      <div>
        <h1 className="font-serif text-3xl sm:text-4xl tracking-tight text-foreground">Configuración de la academia</h1>
        <p className="text-base text-muted-foreground mt-1">Lo que vale para todos los cursos. Cada curso puede tener su propia reserva.</p>
      </div>

      <Section title="General">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Nombre de la academia</Label>
            <Input value={s.academy_name} onChange={set("academy_name")} />
          </div>
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Reserva (USD)</Label>
            <Input type="number" min="0" value={s.reservation_amount} onChange={set("reservation_amount")} data-testid={ACADEMY.settingsReservationInput} />
          </div>
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Horas para responder si no asiste</Label>
            <Input type="number" min="1" value={s.no_show_hours} onChange={set("no_show_hours")} />
          </div>
        </div>
      </Section>

      <Section title="Comisiones de los ejecutivos" text="Solo sobre lo cobrado en el mes. Lo vendido que se cobra después suma comisión cuando se cobra.">
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="radio" name="mode" checked={s.commission_mode === "flat"} onChange={() => setS((x) => ({ ...x, commission_mode: "flat" }))} data-testid={ACADEMY.settingsModeFlat} />
            El porcentaje del tramo alcanzado se aplica a todo lo cobrado del mes
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="radio" name="mode" checked={s.commission_mode === "marginal"} onChange={() => setS((x) => ({ ...x, commission_mode: "marginal" }))} data-testid={ACADEMY.settingsModeMarginal} />
            Cada tramo se aplica solo a su parte
          </label>
        </div>
        <div className="space-y-2" data-testid={ACADEMY.settingsTiers}>
          {s.commission_tiers.map((t, i) => {
            const last = i === s.commission_tiers.length - 1;
            return (
              <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center">
                {last ? <p className="text-sm text-muted-foreground px-1">Más de {s.commission_tiers[i - 1]?.up_to || 0}</p>
                  : <Input type="number" min="0" value={t.up_to} onChange={(e) => setTier(i, "up_to", e.target.value)} placeholder="Hasta (USD)" />}
                <div className="flex items-center gap-1"><Input type="number" min="0" max="100" step="0.5" value={t.percent} onChange={(e) => setTier(i, "percent", e.target.value)} /><span className="text-muted-foreground">%</span></div>
                {!last ? <Button variant="ghost" size="icon" onClick={() => setS((x) => ({ ...x, commission_tiers: x.commission_tiers.filter((_, j) => j !== i) }))}><Trash2 className="w-4 h-4 text-destructive" /></Button> : <span className="w-9" />}
              </div>
            );
          })}
          <Button variant="outline" size="sm" onClick={() => setS((x) => ({ ...x, commission_tiers: [...x.commission_tiers.slice(0, -1), { up_to: "", percent: "" }, x.commission_tiers[x.commission_tiers.length - 1]] }))}>
            <Plus className="w-4 h-4" /> Agregar tramo
          </Button>
        </div>
        {/* Fix X (2026-10-13) */}
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={s.commission_include_reservations !== false} onChange={(e) => setS((x) => ({ ...x, commission_include_reservations: e.target.checked }))} data-testid={ACADEMY.settingsIncludeReservations} />
          La reserva suma para la comisión
        </label>
      </Section>

      {/* Fix X (2026-10-13) */}
      <Section title="Instructores" text="El porcentaje de cada instructor se carga por curso en Cursos y fechas.">
        <div className="max-w-sm">
          <Label className="text-sm text-muted-foreground mb-1 block">El porcentaje se calcula sobre</Label>
          <select className={selectCls} value={s.instructor_base || "collected"} onChange={(e) => setS((x) => ({ ...x, instructor_base: e.target.value }))} data-testid={ACADEMY.settingsInstructorBase}>
            <option value="collected">Lo cobrado de los alumnos de esa fecha</option>
            <option value="price">El precio de los alumnos de esa fecha (cobrado o no)</option>
          </select>
        </div>
      </Section>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Section title="Cómo llegan los interesados" text="Una opción por línea.">
          <Textarea rows={6} value={sources} onChange={(e) => setSources(e.target.value)} />
        </Section>
        <Section title="Categorías de gastos" text="Una por línea.">
          <Textarea rows={6} value={categories} onChange={(e) => setCategories(e.target.value)} />
        </Section>
      </div>

      <Section title="Formulario del alumno" text="Los datos que completa el alumno para la certificación. Un curso puede tener su propio formulario.">
        <div className="space-y-2" data-testid={ACADEMY.settingsFormFields}>
          {s.form_fields.map((f, i) => (
            <div key={i} className="grid grid-cols-1 sm:grid-cols-[2fr_1fr_auto_auto] gap-2 items-center border-b border-border pb-2">
              <Input value={f.label} onChange={(e) => setField(i, "label", e.target.value)} placeholder="Pregunta" />
              <select className={selectCls} value={f.type} onChange={(e) => setField(i, "type", e.target.value)}>
                {Object.entries(FIELD_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              <label className="flex items-center gap-1 text-xs text-muted-foreground whitespace-nowrap"><input type="checkbox" checked={Boolean(f.required)} onChange={(e) => setField(i, "required", e.target.checked)} /> Obligatorio</label>
              <div className="flex">
                {i > 0 && <Button variant="ghost" size="icon" onClick={() => moveField(i)}><ArrowUp className="w-4 h-4" /></Button>}
                <Button variant="ghost" size="icon" onClick={() => setS((x) => ({ ...x, form_fields: x.form_fields.filter((_, j) => j !== i) }))}><Trash2 className="w-4 h-4 text-destructive" /></Button>
              </div>
              {f.type === "select" && (
                <Input className="sm:col-span-4" value={Array.isArray(f.options) ? f.options.join(", ") : f.options} onChange={(e) => setField(i, "options", e.target.value)} placeholder="Opciones separadas por coma" />
              )}
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={() => setS((x) => ({ ...x, form_fields: [...x.form_fields, { key: null, label: "", type: "text", required: false, options: [] }] }))}>
            <Plus className="w-4 h-4" /> Agregar pregunta
          </Button>
        </div>
      </Section>

      <Section title="Contrato" text="Texto que firma el alumno antes de confirmar la inscripción. Lo provee la academia. Un curso puede tener su propio texto.">
        <Textarea rows={10} value={s.contract_text || ""} onChange={set("contract_text")} placeholder="Pegá acá el texto del contrato" data-testid={ACADEMY.settingsContractInput} />
      </Section>

      <Section title="Emails al alumno">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Nombre del remitente</Label>
            <Input value={s.email_from_name || ""} onChange={set("email_from_name")} />
          </div>
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Responder a (email de la academia)</Label>
            <Input type="email" value={s.email_reply_to || ""} onChange={set("email_reply_to")} />
          </div>
        </div>
        <EmailCard replyTo={s.email_reply_to} />
      </Section>

      {/* Fix Z (2026-10-13) */}
      <Section title="Respaldo en Notion" text="Una página por alumno con sus datos, inscripciones, pagos y saldo. Compartí la base de datos con la integración de Notion y pegá acá su link.">
        <div>
          <Label className="text-sm text-muted-foreground mb-1 block">Link de la base de datos de Notion</Label>
          <Input value={s.notion_database_id || ""} onChange={set("notion_database_id")} placeholder="https://www.notion.so/..." data-testid={ACADEMY.settingsNotionInput} />
        </div>
        <NotionCard savedDatabaseId={s.saved_notion_id} />
      </Section>

      <div className="flex justify-end">
        <Button className={btnBrand} onClick={save} disabled={saving} data-testid={ACADEMY.settingsSaveButton}>{saving ? "Guardando..." : "Guardar configuración"}</Button>
      </div>
    </div>
  );
}
